import { Machine } from '@internal/utils/machine/Machine';
import { getMediaSrc } from '@internal/utils/media/getMediaSrc';
import { RolloutsContext } from '@internal/utils/rollouts/RolloutsContext';
import { Container, Grid, Typography } from '@mui/material';
import { Icon } from '@renderer-ui-library/atoms';
import { ActionableButton, ProductCard } from '@renderer-ui-library/molecules';
import { useMediaQuery } from '@renderer-ui-library/mui/utils/mediaQuery/useMediaQuery';
import { BlockWrapper } from '@renderer-ui-library/scaffolding/BlockWrapper/BlockWrapper';
import { BlockProps } from 'blocks/BlockProps';
import React, { FC, useContext } from 'react';
import 'swiper/css';
import 'swiper/css/thumbs';
import 'swiper/css/zoom';
import {
    Autoplay,
    EffectCoverflow,
    Keyboard,
    Mousewheel,
    Pagination,
} from 'swiper/modules';
import { Swiper, SwiperClass, SwiperSlide } from 'swiper/react';
import { IMachinesSliderCoverBlockData } from './IMachinesSliderCoverBlockData';
import styles from './machineSliderCoverBlockStyles.module.scss';
import { TServerSideMachinesSliderCoverBlockProps } from './TServerSideMachinesSliderCoverBlockProps';

const SwiperWrapper: FC<{
    machines: Machine[];
    variant: 'desktop' | 'mobile';
}> = ({ machines, variant }) => {
    const handleSlideChange = React.useCallback(
        (swiper: SwiperClass) => {
            // on desktop we want to stop autoplay before we get whitespace on the right
            if (
                variant === 'desktop' &&
                machines.length > 4 &&
                swiper.activeIndex >= machines.length - 3
            ) {
                swiper.autoplay.stop();
            }
        },
        [machines.length, variant],
    );

    return (
        <Swiper
            onSlideChange={handleSlideChange}
            slidesPerView="auto"
            modules={[
                Keyboard,
                Mousewheel,
                EffectCoverflow,
                Pagination,
                Autoplay,
            ]}
            keyboard={{
                enabled: true,
            }}
            mousewheel={{
                enabled: true,
            }}
            pagination
            autoplay={
                variant === 'mobile' || machines.length > 4
                    ? {
                          delay: 1500,
                          disableOnInteraction: true,
                          pauseOnMouseEnter: true,
                          stopOnLastSlide: true,
                      }
                    : undefined
            }
            spaceBetween={variant === 'desktop' ? 0 : 8}
            effect={variant === 'desktop' ? 'coverflow' : 'slide'}
            coverflowEffect={{
                rotate: 10,
                stretch: 0,
                depth: 100,
                modifier: 1,
                slideShadows: true,
                scale: 0.9,
            }}
            initialSlide={variant === 'desktop' ? 2 : 0}
            centeredSlides
            className={styles.swiper}
        >
            {machines.map(machine => (
                <SwiperSlide
                    className={styles.slide}
                    key={machine.machineGroupId}
                >
                    <ProductCard machine={machine} />
                </SwiperSlide>
            ))}
        </Swiper>
    );
};

export const MachinesSliderCoverBlock: React.FC<
    BlockProps<
        IMachinesSliderCoverBlockData,
        TServerSideMachinesSliderCoverBlockProps
    >
> = React.memo(props => {
    const { newSearch } = useContext(RolloutsContext);
    const { isMobile } = useMediaQuery();

    if (!newSearch) {
        return null;
    }

    return (
        <BlockWrapper
            blockData={props.data}
            noSpacingTop
            noSpacingBottom
            fullWidth
        >
            <div className={styles.wrapper}>
                {!!props.data.backgroundMedia?.files?.data?.length && (
                    <div className={styles.mediaWrapper}>
                        {props.data.backgroundMedia.files.data.map(file => (
                            <img
                                loading="eager"
                                key={file.id}
                                alt=""
                                {...getMediaSrc(file, {
                                    ratio: 'original',
                                    sizeSet: 'extraLarge',
                                })}
                            />
                        ))}
                    </div>
                )}

                <div className={styles.contentWrapper}>
                    <Container maxWidth="lg" disableGutters>
                        <div className={styles.elementsWrapper}>
                            {props.data.title && (
                                <Typography
                                    variant="h3"
                                    className={styles.title}
                                    component="h1"
                                    color="inherit"
                                >
                                    {props.data.title}
                                </Typography>
                            )}
                            {props.data.subtitle && (
                                <Typography
                                    variant={isMobile ? 'body1' : 'h6'}
                                    className={styles.title}
                                    component="p"
                                    color="inherit"
                                >
                                    {props.data.subtitle}
                                </Typography>
                            )}

                            <div className={styles.columnsWrapper}>
                                <Grid
                                    container
                                    columnSpacing={2}
                                    rowSpacing={{ xs: 1, lg: 4 }}
                                >
                                    {props.data.columns.map(column => (
                                        <Grid
                                            item
                                            xs={12}
                                            md={3}
                                            key={column.id}
                                            className={styles.column}
                                        >
                                            <Icon
                                                icon={column.icon}
                                                className={styles.icon}
                                            />
                                            {column.text && (
                                                <Typography
                                                    variant="body1"
                                                    color="inherit"
                                                    component="p"
                                                    fontWeight={500}
                                                >
                                                    {column.text}
                                                </Typography>
                                            )}
                                        </Grid>
                                    ))}
                                </Grid>
                            </div>

                            {props.preFetchedBlockProps.machines.length > 0 && (
                                <div className={styles.sliderWrapper}>
                                    {props.data.machinesTitle && (
                                        <Typography
                                            variant="h3"
                                            component="h2"
                                            color="inherit"
                                        >
                                            {props.data.machinesTitle}
                                        </Typography>
                                    )}
                                    <div className={styles.swiperWrapper}>
                                        {/* We change the component, not just the variant prop, because we want the whole thing to re-init */}
                                        {isMobile && (
                                            <SwiperWrapper
                                                machines={
                                                    props.preFetchedBlockProps
                                                        .machines
                                                }
                                                variant="mobile"
                                            />
                                        )}
                                        {!isMobile && (
                                            <SwiperWrapper
                                                machines={
                                                    props.preFetchedBlockProps
                                                        .machines
                                                }
                                                variant="desktop"
                                            />
                                        )}
                                    </div>

                                    {props.data.cta && (
                                        <div className={styles.buttonWrapper}>
                                            <ActionableButton
                                                button={props.data.cta}
                                                color="secondary"
                                                variant="contained"
                                            />
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    </Container>
                </div>
            </div>
        </BlockWrapper>
    );
});

MachinesSliderCoverBlock.displayName = 'MachinesSliderCoverBlock';
